import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';

export type MessageSender = 'user' | 'bot';
export type MessageType = 'text' | 'actions' | 'ticket_escalation' | 'guided_nav' | 'hint' | 'faq';

export interface Message {
  id: string;
  sender: MessageSender;
  text: string;
  timestamp: number;
  type?: MessageType;
}

interface AskKikiContextProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  messages: Message[];
  addMessage: (msg: Omit<Message, 'id' | 'timestamp'>) => void;
  isTyping: boolean;
  setIsTyping: (isTyping: boolean) => void;
  unreadCount: number;
  setUnreadCount: (count: number | ((prev: number) => number)) => void;
  showNavOverlay: boolean;
  setShowNavOverlay: (show: boolean) => void;
}

const AskKikiContext = createContext<AskKikiContextProps | undefined>(undefined);

const initialMessages: Message[] = [
  {
    id: 'welcome-msg',
    sender: 'bot',
    text: '👋 Welcome to Marketing 4SIGHT\nI am Kiki, your smart marketing buddy.\nChoose a topic below or ask me anything about the platform, pricing, onboarding, or support.',
    timestamp: Date.now(),
    type: 'text',
  },
  {
    id: 'root-nav',
    sender: 'bot',
    text: 'root', // This will tell ChatWindow to render the root navigation config
    timestamp: Date.now() + 1,
    type: 'guided_nav',
  }
];

const loadMessages = (): Message[] => {
  const isAuth = !!localStorage.getItem('access_token');
  if (isAuth) {
    const userId = localStorage.getItem('user_id') || 'default';
    const msgKey = `ask_kiki_messages_${userId}`;
    const timeKey = `ask_kiki_timestamp_${userId}`;
    
    const stored = localStorage.getItem(msgKey);
    const timestamp = localStorage.getItem(timeKey);
    
    if (stored && timestamp) {
      const timeDiff = Date.now() - parseInt(timestamp, 10);
      if (timeDiff < 2 * 60 * 60 * 1000) { // 2 hours
        try {
          return JSON.parse(stored);
        } catch (e) {
          console.error('Failed to parse Ask Kiki messages from localStorage');
        }
      } else {
        localStorage.removeItem(msgKey);
        localStorage.removeItem(timeKey);
      }
    }
  }
  return initialMessages;
};

export const AskKikiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showNavOverlay, setShowNavOverlay] = useState(false);
  const [messages, setMessages] = useState<Message[]>(loadMessages);
  const [isTyping, setIsTyping] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const isAuth = !!localStorage.getItem('access_token');
    const userId = localStorage.getItem('user_id') || 'default';
    const msgKey = `ask_kiki_messages_${userId}`;
    const timeKey = `ask_kiki_timestamp_${userId}`;

    if (isAuth) {
      localStorage.setItem(msgKey, JSON.stringify(messages));
      localStorage.setItem(timeKey, Date.now().toString());
    } else {
      // In case they log out while the chat widget is mounted,
      // though typically a full refresh happens on logout
      localStorage.removeItem(msgKey);
      localStorage.removeItem(timeKey);
    }
  }, [messages]);

  const addMessage = (msg: Omit<Message, 'id' | 'timestamp'>) => {
    setMessages((prev) => [
      ...prev,
      {
        ...msg,
        id: Math.random().toString(36).substring(2, 9),
        timestamp: Date.now(),
      },
    ]);
    if (!isOpen && msg.sender === 'bot') {
      setUnreadCount((prev) => prev + 1);
    }
  };

  return (
    <AskKikiContext.Provider
      value={{
        isOpen,
        setIsOpen,
        messages,
        addMessage,
        isTyping,
        setIsTyping,
        unreadCount,
        setUnreadCount,
        showNavOverlay,
        setShowNavOverlay,
      }}
    >
      {children}
    </AskKikiContext.Provider>
  );
};

export const useAskKiki = () => {
  const context = useContext(AskKikiContext);
  if (!context) {
    throw new Error('useAskKiki must be used within an AskKikiProvider');
  }
  return context;
};
